import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Code2, Briefcase, Globe, Server, Cpu, Wifi, Database, Shield, ChevronRight, ExternalLink } from 'lucide-react';

const slides = [
  {
    id: 1,
    theme: 'dev',
    badge: 'Solutions Informatiques',
    title: 'Développement de Solutions',
    highlight: 'Sur Mesure',
    description: 'Nous concevons des applications web, mobiles et des systèmes d\'information adaptés à votre métier — performants, évolutifs, et livrés dans les délais.',
    cta1: { label: 'Voir nos réalisations', href: '/ict-consulting' },
    cta2: { label: 'Demander un devis', href: '/rejoignez-nous' },
    accent: '#E8611A',
    icons: [Code2, Cpu, Database, Shield],
    gradient: 'from-[#0a0f1e] via-[#0f1a2e] to-[#0a0f1e]',
    circleColor: '#E8611A',
  },
  {
    id: 2,
    theme: 'emploi',
    badge: 'NigerEmploi',
    title: 'Publiez Vos Offres',
    highlight: "d'Emploi",
    description: 'NigerEmploi est le premier site d\'emploi et de recrutement en ligne au Niger. Publiez vos offres et atteignez des milliers de candidats qualifiés à travers le pays.',
    cta1: { label: 'Publier une offre', href: 'https://nigeremploi.net', external: true },
    cta2: { label: 'En savoir plus', href: '/rejoignez-nous' },
    accent: '#16a34a',
    icons: [Briefcase, Globe, Wifi, Cpu],
    gradient: 'from-[#071a0f] via-[#0b1f14] to-[#071a0f]',
    circleColor: '#16a34a',
  },
  {
    id: 3,
    theme: 'hosting',
    badge: 'Sesame Hosting',
    title: 'Hébergement & Noms',
    highlight: 'de Domaine',
    description: 'Sesame Hosting vous offre une infrastructure  fiable, des noms de domaine professionnels et une mise en ligne rapide pour votre présence digitale.',
    cta1: { label: 'Commander maintenant', href: 'https://sesamehosting.com/', external: true },
    cta2: { label: 'Voir les offres', href: '/rejoignez-nous' },
    accent: '#E8611A',
    icons: [Server, Globe, Database, Shield],
    gradient: 'from-[#160a1e] via-[#1a0f2e] to-[#160a1e]',
    circleColor: '#E8611A',
  },
];

/* ── Animated background SVG patterns per slide ───────────────────── */
const TechPattern = ({ accent }) => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id={`grid-${accent}`} width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M 60 0 L 0 0 0 60" fill="none" stroke={accent} strokeWidth="0.8" />
      </pattern>
      <pattern id={`dots-${accent}`} width="30" height="30" patternUnits="userSpaceOnUse">
        <circle cx="15" cy="15" r="1.2" fill={accent} />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#grid-${accent})`} />
    <rect width="100%" height="100%" fill={`url(#dots-${accent})`} opacity="0.5" />
  </svg>
);

const CircuitLines = ({ accent }) => (
  <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice">
    {/* Horizontal traces */}
    <line x1="0" y1="120" x2="400" y2="120" stroke={accent} strokeWidth="1.5" />
    <line x1="400" y1="120" x2="400" y2="200" stroke={accent} strokeWidth="1.5" />
    <line x1="400" y1="200" x2="700" y2="200" stroke={accent} strokeWidth="1.5" />
    <line x1="700" y1="200" x2="700" y2="350" stroke={accent} strokeWidth="1.5" />
    <line x1="700" y1="350" x2="1200" y2="350" stroke={accent} strokeWidth="1.5" />
    {/* More traces */}
    <line x1="200" y1="0" x2="200" y2="400" stroke={accent} strokeWidth="1" />
    <line x1="900" y1="100" x2="900" y2="700" stroke={accent} strokeWidth="1" />
    <line x1="0" y1="500" x2="550" y2="500" stroke={accent} strokeWidth="1.5" />
    <line x1="550" y1="500" x2="550" y2="600" stroke={accent} strokeWidth="1.5" />
    <line x1="550" y1="600" x2="1200" y2="600" stroke={accent} strokeWidth="1.5" />
    {/* Nodes */}
    {[[400,120],[400,200],[700,200],[700,350],[200,400],[900,350],[550,500],[550,600]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="4" fill={accent} />
    ))}
  </svg>
);

/* ── Floating icons ring ───────────────────────────────────────────── */
const FloatingRing = ({ icons, accent, active }) => {
  const radius = 220;
  return (
    <div className="absolute right-[-60px] sm:right-[0px] md:right-[60px] lg:right-[100px] top-1/2 -translate-y-1/2 w-[340px] h-[340px] md:w-[440px] md:h-[440px] hidden md:block">
      {/* Outer glow ring */}
      <div
        className="absolute inset-0 rounded-full border opacity-20 transition-all duration-700"
        style={{ borderColor: accent, boxShadow: `0 0 60px ${accent}33` }}
      />
      <div
        className="absolute inset-[40px] rounded-full border opacity-10 transition-all duration-700"
        style={{ borderColor: accent }}
      />
      {/* Center CPU graphic */}
      <div
        className="absolute inset-[80px] rounded-full flex items-center justify-center transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${accent}22 0%, transparent 70%)`, borderColor: `${accent}44`, border: '1px solid' }}
      >
        <div className="text-center">
          <Cpu size={56} style={{ color: accent }} className="mx-auto mb-1 opacity-80" />
          <span className="text-white/40 text-xs font-mono tracking-widest">AYDEES</span>
        </div>
      </div>
      {/* Orbiting icons */}
      {icons.map((Icon, i) => {
        const angle = (i / icons.length) * 360 + (active ? 10 : 0);
        const rad = (angle * Math.PI) / 180;
        const x = Math.cos(rad) * (radius / 2 - 10) + 170;
        const y = Math.sin(rad) * (radius / 2 - 10) + 170;
        return (
          <div
            key={i}
            className="absolute w-10 h-10 rounded-full flex items-center justify-center transition-all duration-700"
            style={{
              left: x - 20,
              top: y - 20,
              background: `${accent}22`,
              border: `1px solid ${accent}55`,
              boxShadow: `0 0 14px ${accent}44`,
            }}
          >
            <Icon size={18} style={{ color: accent }} />
          </div>
        );
      })}
    </div>
  );
};

export default function ImageSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('next');
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  const go = (next, dir = 'next') => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setVisible(false);
    setTimeout(() => {
      setCurrentIndex(next);
      setVisible(true);
      setAnimating(false);
    }, 400);
  };

  const nextSlide = () => go((currentIndex + 1) % slides.length, 'next');
  const prevSlide = () => go((currentIndex - 1 + slides.length) % slides.length, 'prev');

  useEffect(() => {
    timerRef.current = setInterval(nextSlide, 6000);
    return () => clearInterval(timerRef.current);
  }, [currentIndex]);

  const slide = slides[currentIndex];

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ minHeight: 'min(100vh, 680px)', height: 'clamp(560px, 90vh, 700px)' }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 transition-all duration-700"
        style={{ background: `radial-gradient(ellipse at 60% 50%, ${slide.accent}15 0%, transparent 60%), linear-gradient(135deg, #07090f 0%, #0d1220 50%, #07090f 100%)` }}
      />
      <TechPattern accent={slide.accent} />
      <CircuitLines accent={slide.accent} />

      {/* Glow blobs */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl transition-all duration-1000 pointer-events-none"
        style={{ background: slide.accent }}
      />
      <div
        className="absolute -bottom-32 right-1/3 w-96 h-96 rounded-full opacity-10 blur-3xl transition-all duration-1000 pointer-events-none"
        style={{ background: slide.accent }}
      />

      {/* Floating ring - desktop */}
      <FloatingRing icons={slide.icons} accent={slide.accent} active={visible} />

      {/* Content */}
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full">
          <div
            className="max-w-2xl transition-all duration-500"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible
                ? 'translateY(0)'
                : direction === 'next' ? 'translateY(24px)' : 'translateY(-24px)',
            }}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-5"
              style={{ background: `${slide.accent}22`, border: `1px solid ${slide.accent}55`, color: slide.accent }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: slide.accent }} />
              {slide.badge}
            </div>

            {/* Heading */}
            <h1 className="font-black leading-tight mb-2">
              <span
                className="block text-white"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3.6rem)',
                  fontFamily: "'Rajdhani', 'Exo 2', sans-serif",
                  letterSpacing: '-0.01em',
                  textShadow: '0 2px 40px rgba(0,0,0,0.6)',
                }}
              >
                {slide.title}
              </span>
              <span
                className="block"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3.6rem)',
                  fontFamily: "'Rajdhani', 'Exo 2', sans-serif",
                  letterSpacing: '-0.01em',
                }}
              >
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${slide.accent}, ${slide.accent}bb)`,
                  }}
                >
                  {slide.highlight}
                </span>
              </span>
            </h1>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-0.5" style={{ background: slide.accent }} />
              <div className="w-2 h-2 rounded-full" style={{ background: slide.accent }} />
              <div className="w-4 h-0.5" style={{ background: `${slide.accent}55` }} />
            </div>

            {/* Description */}
            <p className="text-white/70 leading-relaxed mb-8 max-w-xl" style={{ fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)' }}>
              {slide.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <a
                href={slide.cta1.href}
                target={slide.cta1.external ? "_blank" : undefined}
                rel={slide.cta1.external ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-2 font-bold rounded-full transition-all duration-300 hover:gap-3 hover:scale-105 hover:shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${slide.accent}, ${slide.accent}cc)`,
                  color: '#fff',
                  padding: '0.75rem 1.75rem',
                  fontSize: '0.9rem',
                  boxShadow: `0 8px 24px ${slide.accent}44`,
                }}
              >
                {slide.cta1.label}
                <ChevronRight size={16} />
              </a>
              <a
                href={slide.cta2.href}
                target={slide.cta2.external ? "_blank" : undefined}
                rel={slide.cta2.external ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-2 font-medium rounded-full transition-all duration-300 hover:bg-white/10 hover:gap-3"
                style={{
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: 'rgba(255,255,255,0.85)',
                  padding: '0.75rem 1.75rem',
                  fontSize: '0.9rem',
                }}
              >
                {slide.cta2.label}
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Arrow controls */}
      <button
        onClick={prevSlide}
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-20"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}
      >
        <ArrowLeft size={18} className="text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-20"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}
      >
        <ArrowRight size={18} className="text-white" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-20">
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => go(i, i > currentIndex ? 'next' : 'prev')}
            className="transition-all duration-400 rounded-full"
            style={{
              width: i === currentIndex ? '32px' : '8px',
              height: '8px',
              background: i === currentIndex ? s.accent : 'rgba(255,255,255,0.3)',
              boxShadow: i === currentIndex ? `0 0 10px ${s.accent}88` : 'none',
            }}
          />
        ))}
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-6 right-6 text-white/30 text-xs font-mono tracking-widest z-20 hidden sm:block">
        0{currentIndex + 1} / 0{slides.length}
      </div>

      {/* Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@700;800;900&display=swap');
      `}</style>
    </div>
  );
}